import type { EntityData } from '@adornis/baseql/entities/types.js';
import { getRawCollection } from '@adornis/baseql/server/collections.js';
import { DesignSystem } from '@adornis/chemistry/elements/theming/design.js';
import { defineMailTemplate } from '@adornis/mails/db/mail-template-definition.js';
import type { EvaluationTermin } from '@ils/evaluation-scheduler/db/EvaluationTermin.js';
import { getInspectorWarningMailData } from '@ils/evaluation-scheduler/server/mailData.js';
import { EmailType, EmailVars } from '@ils/mails/emailEnums.js';
import { Specialization } from '@ils/static/enums.js';
import { Names } from '@ils/static/names.js';
import type { ILSUser } from '@legacy-db/ils-user.js';
import { html } from 'lit';
import { DateTime } from 'luxon';

const transformDate = (date: Date) => {
  return DateTime.fromJSDate(date).setLocale('de-DE').toLocaleString(DateTime.DATETIME_MED);
};

const transformDurationToMins = (duration: { start: Date; end: Date }) => {
  return millisToMinutes(duration.end.getTime() - duration.start.getTime());
};

const millisToMinutes = (millis: number) => {
  const seconds = millis / 1000;
  const minutes = seconds / 60;
  return minutes;
};

const TerminCard = `<div style="background: #fff; border-radius: 8px; padding: 24px; border-left: 3px solid ${
  DesignSystem.currentTheme.getValue().colors.accent
}">
<table>
  <tr>
    <td> Mediziner </td>
    <td> {{${EmailVars.FIRSTNAME_DOCTOR_INSPECTOR}}} {{${EmailVars.LASTNAME_DOCTOR_INSPECTOR}}} </td>
  </tr>
  <tr>
    <td> Linguist </td>
    <td> {{${EmailVars.FIRSTNAME_LINGUIST_INSPECTOR}}} {{${EmailVars.LASTNAME_LINGUIST_INSPECTOR}}} </td>
  </tr>
  <tr>
    <td> Zeitpunkt </td>
    <td> {{${EmailVars.DATE}}} </td>
  </tr>
  <tr>
    <td> Ungefähre Dauer </td>
    <td> {{${EmailVars.DURATION}}} minuten </td>
  </tr>
  <tr>
    <td> Anzahl zu bewertende Slots </td>
    <td> {{${EmailVars.SLOT_COUNT}}} </td>
  </tr>
</table>
</div>`;

const getTerminBewertungData = async (termin: EntityData<EvaluationTermin>) => {
  const rawUserCollection = await getRawCollection<EntityData<ILSUser>>(Names.ILSUser._collectionName);
  const inspectors = await rawUserCollection.find({ _id: { $in: termin.userIDs } }).toArray();

  const doctor = inspectors.find(i => i.specialization === Specialization.DOCTOR);
  const linguist = inspectors.find(i => i.specialization === Specialization.LINGUIST);

  if (!doctor || !linguist) throw new Error('doctor or linguist was not found');
  return {
    data: {
      [EmailVars.FIRSTNAME_DOCTOR_INSPECTOR]: doctor.ecommerce?.firstName,
      [EmailVars.LASTNAME_DOCTOR_INSPECTOR]: doctor.ecommerce?.lastName,
      [EmailVars.FIRSTNAME_LINGUIST_INSPECTOR]: linguist.ecommerce?.firstName,
      [EmailVars.LASTNAME_LINGUIST_INSPECTOR]: linguist.ecommerce?.lastName,
      [EmailVars.DATE]: transformDate(termin.start),
      [EmailVars.DURATION]: transformDurationToMins(termin).toString(),
      [EmailVars.SLOT_COUNT]: termin.slotCount.toString(),
    },
  };
};

export const InspectorWarningMail = defineMailTemplate(EmailType.TERMIN_BEWERTUNG_WARNING, {
  displayName: EmailType.TERMIN_BEWERTUNG_WARNING,
  defaultTemplate: {
    subject: 'Termin Warnung!',
    content: `
<p>Termin Warnung!</p>
<p>Es existieren {{${EmailVars.SLOT_COUNT}}} Situationen, für welche seit mehr als 7 Tagen kein passender Termin gefunden werden konnte.</p>
<p>Viele Grüße, Ihr FaMed-Team</p>
`,
    footer: '',
  },
  variables: [EmailVars.SLOT_COUNT, EmailVars.SEVEN_DAY_WARNING_RATING_LIST],
  attachments: [],
  editorHint: () => html`
    <p>
      Wird gesendet an:<br />
      - Betroffene Bewerter (Mediziner & Linguist)<br />
      - Admin
    </p>
    <p>
      Wann:<br />
      - sobald für einen oder mehrere Slot nach einer Woche kein passender Termin gefunden werden konnte.
    </p>
  `,
  getMailData: getInspectorWarningMailData,
});

export const AdminWarningMail = defineMailTemplate(EmailType.TERMIN_BEWERTUNG_WARNING_ADMIN, {
  displayName: EmailType.TERMIN_BEWERTUNG_WARNING_ADMIN,
  defaultTemplate: {
    subject: 'Termin Warnung!',
    content: `
<p>Termin Warnung!</p>
<p>Einer oder mehrere Situationen haben vier Wochen nach dem Prüfungstermin keine abgeschlossene Bewertung.</p>
<div style="background: #fff; border-radius: 8px; padding: 24px; border-left: 3px solid ${
      DesignSystem.currentTheme.getValue().colors.accent
    }">
{{${EmailVars.FOUR_WEEK_WARNING_EXAMDAY_LIST}}}
</div>
<p>Viele Grüße, Ihr FaMed-Team</p>
`,
    footer: '',
  },
  variables: [EmailVars.FOUR_WEEK_WARNING_EXAMDAY_LIST],
  attachments: [],
  editorHint: () => html`
    <p>
      Wird gesendet an:<br />
      - Admin
    </p>
    <p>
      Wann:<br />
      - sobald ein oder mehrere Slots vier Wochen nach dem Prüfungstermin keine abgeschlossene Bewertung haben.
    </p>
  `,
  getMailData: (
    data: Array<{
      _id: string;
      name: string;
      start: Date;
      slots: Array<{ _id: string; candidateID: string; osceID: string; osceGroupID: string }>;
    }>,
  ) => {
    return {
      data: {
        [EmailVars.FOUR_WEEK_WARNING_EXAMDAY_LIST]: ` <table>
  <tr> <th> Prüfungstag </th> <th> Anzahl unbewerteter Situationen </th> </tr>
  ${data.map(d => `<tr> <td> ${transformDate(d.start)} </td> <td> ${d.slots.length} </td> </tr>`).join('\n')}
</table> `,
      },
    };
  },
});

export const TerminCreatedMail = defineMailTemplate(EmailType.TERMIN_BEWERTUNG_BESTAETIGUNG, {
  displayName: EmailType.TERMIN_BEWERTUNG_BESTAETIGUNG,
  defaultTemplate: {
    subject: 'Terminbestätigung',
    content: `
<p>Bewertungstermin Bestätigung!</p>
<p>Ein Termin zur Bewertung wurde für den {{${EmailVars.DATE}}} angelegt.</p>
${TerminCard}
<p>Viele Grüße, Ihr FaMed-Team</p>
`,
    footer: '',
  },
  variables: [
    EmailVars.FIRSTNAME_DOCTOR_INSPECTOR,
    EmailVars.LASTNAME_DOCTOR_INSPECTOR,
    EmailVars.FIRSTNAME_LINGUIST_INSPECTOR,
    EmailVars.LASTNAME_LINGUIST_INSPECTOR,
    EmailVars.DATE,
    EmailVars.DURATION,
    EmailVars.SLOT_COUNT,
  ],
  attachments: [],
  editorHint: () => html`
    <p>
      Wird gesendet an:<br />
      - Betroffene Bewerter (Mediziner & Linguist)
    </p>
    <p>
      Wann:<br />
      - sobald ein Termin zur Bewertung erstellt wurde.
    </p>
  `,
  getMailData: getTerminBewertungData,
});

export const TerminCanceledMail = defineMailTemplate(EmailType.TERMIN_BEWERTUNG_ABGESAGT, {
  displayName: EmailType.TERMIN_BEWERTUNG_ABGESAGT,
  defaultTemplate: {
    subject: 'Bewertungstermin abgesagt',
    content: `
<p>Bewertungstermin abgesagt!</p>
<p>Der Termin am {{${EmailVars.DATE}}} wurde abgesagt.</p>
${TerminCard}
<p>Viele Grüße, Ihr FaMed-Team</p>
`,
    footer: '',
  },
  variables: [
    EmailVars.FIRSTNAME_DOCTOR_INSPECTOR,
    EmailVars.LASTNAME_DOCTOR_INSPECTOR,
    EmailVars.FIRSTNAME_LINGUIST_INSPECTOR,
    EmailVars.LASTNAME_LINGUIST_INSPECTOR,
    EmailVars.DATE,
    EmailVars.DURATION,
    EmailVars.SLOT_COUNT,
  ],
  attachments: [],
  editorHint: () => html`
    <p>
      Wird gesendet an:<br />
      - Betroffene Bewerter (Mediziner & Linguist)
    </p>
    <p>
      Wann:<br />
      - sobald ein Termin zur Bewertung von einer Partei abgesagt wurde.
    </p>
  `,
  getMailData: getTerminBewertungData,
});
